import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Card from '../../common/cards/Card'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'
import Button from '../../common/form/inputs/Button'
import { faFilter, faSliders } from '@fortawesome/free-solid-svg-icons'
import { PersonLiteDto } from '../../../api/person/models/PersonDto'
import PersonBadge from '../../common/person/PersonBadge'
import ExpandableText from '../../common/expandable/ExpandableText'
import SearchFiltersModal from './SearchFiltersModal'
import {
    createDefaultFilterInputs,
    type ExperienceBounds,
    type FilterInputs,
    type SearchInvocationOptions,
} from './filterTypes'
import {
    ExcludeCompanyBadge,
    ExcludeJobTitleBadge,
    ExperienceBadge,
    IncludeCompanyBadge,
    IncludeJobTitleBadge,
    JobFunctionBadge,
    KeywordGroupBadge,
    SeniorityBadge,
    LocationBadge,
} from './FilterBadges'
import { MapExtra } from '../../../utils/MapExtra'

const getTrustedCirclePersonIds = (
    networkSeedPersons: PersonLiteDto[],
): string[] => {
    const uniqueIds = new Set<string>()

    for (const person of networkSeedPersons) {
        uniqueIds.add(person.id)
    }

    return Array.from(uniqueIds)
}

const areStringArraysEqual = (a: string[], b: string[]): boolean => {
    if (a.length !== b.length) {
        return false
    }

    return a.every((value, index) => value === b[index])
}

type Props = {
    networkSeedPersons: PersonLiteDto[]
    roleId?: string | null
    roleJobDescription?: string | null
    onOpenTrustedCircleModal?: () => void
    openModalSignal?: number
    onSearchWithAi?: (
        nextFilters: FilterInputs,
        jobDescription?: string,
        experience?: ExperienceBounds,
        options?: SearchInvocationOptions,
    ) => void
    onSearch?: (
        filters: FilterInputs,
        experience: ExperienceBounds,
        options?: SearchInvocationOptions,
    ) => void
    savedFilters?: FilterInputs
    onFiltersChange?: (filters: FilterInputs) => void
    experienceMinYears: number
    experienceMaxYears: number
    defaultMinExperienceYears: number
    defaultMaxExperienceYears: number
    onExperienceMinYearsChange: (value: number) => void
    onExperienceMaxYearsChange: (value: number) => void
    onSaveFilters?: (
        nextFilters: FilterInputs,
        experience?: ExperienceBounds,
    ) => Promise<void>
    isSavingFilters?: boolean
    filtersModalError?: string | null
    onFiltersModalClose?: () => void
    networkLinksPersonLiteCache: MapExtra<string, PersonLiteDto>
}

export default function SearchFilters({
    networkSeedPersons,
    roleId,
    roleJobDescription,
    onOpenTrustedCircleModal,
    openModalSignal,
    onSearchWithAi,
    onSearch,
    savedFilters,
    onFiltersChange,
    experienceMinYears,
    experienceMaxYears,
    defaultMinExperienceYears,
    defaultMaxExperienceYears,
    onExperienceMinYearsChange,
    onExperienceMaxYearsChange,
    onSaveFilters,
    isSavingFilters = false,
    filtersModalError,
    onFiltersModalClose,
    networkLinksPersonLiteCache,
}: Props): React.JSX.Element {
    const trustedCirclePersonIds = useMemo(
        () => getTrustedCirclePersonIds(networkSeedPersons),
        [networkSeedPersons],
    )
    const defaultFilters = useMemo(
        () => createDefaultFilterInputs(trustedCirclePersonIds),
        [trustedCirclePersonIds],
    )
    const [filters, setFilters] = useState<FilterInputs>(
        savedFilters ?? defaultFilters,
    )
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
    const isSyncingFiltersRef = useRef(false)
    const lastSyncedTrustedCircleRef = useRef(trustedCirclePersonIds)
    const externalOpenSignalRef = useRef<number | undefined>(openModalSignal)

    useEffect(() => {
        if (openModalSignal === undefined) {
            externalOpenSignalRef.current = undefined
            return
        }
        if (externalOpenSignalRef.current === openModalSignal) {
            return
        }
        externalOpenSignalRef.current = openModalSignal
        setIsFiltersModalOpen(true)
    }, [openModalSignal])

    const handleFiltersSave = (nextFilters: FilterInputs) => {
        setFilters(nextFilters)
        setIsFiltersModalOpen(false)
        onFiltersModalClose?.()
    }

    const handleFiltersModalClose = () => {
        setIsFiltersModalOpen(false)
        onFiltersModalClose?.()
    }

    useEffect(() => {
        if (filtersModalError) {
            setIsFiltersModalOpen(true)
        }
    }, [filtersModalError])

    const handleSearchWithAi = (
        nextFilters: FilterInputs,
        jobDescription?: string,
        experience?: ExperienceBounds,
    ) => {
        setFilters(nextFilters)
        setIsFiltersModalOpen(false)
        onSearchWithAi?.(nextFilters, jobDescription, experience, {
            source: 'filters_modal',
        })
    }

    const networkSeedsMaxHeightPx = 3 * 40 + 2 * 8 // 3 rows of ~40px badges with 8px gaps
    const networkSeedsForDisplay = useMemo(() => {
        return Array.from(new Set(filters.trustedCirclePersonIds))
    }, [filters.trustedCirclePersonIds])
    const hasCustomMinExperience =
        experienceMinYears > defaultMinExperienceYears
    const hasCustomMaxExperience =
        experienceMaxYears < defaultMaxExperienceYears
    const experienceBadgeText = (() => {
        if (!hasCustomMinExperience && !hasCustomMaxExperience) {
            return null
        }

        if (hasCustomMinExperience && hasCustomMaxExperience) {
            return `${experienceMinYears}-${experienceMaxYears} years experience`
        }

        if (hasCustomMinExperience) {
            return `≥ ${experienceMinYears} years experience`
        }

        return `≤ ${experienceMaxYears} years experience`
    })()

    useEffect(() => {
        if (
            areStringArraysEqual(
                lastSyncedTrustedCircleRef.current,
                trustedCirclePersonIds,
            )
        ) {
            return
        }

        lastSyncedTrustedCircleRef.current = trustedCirclePersonIds
        setFilters((prev) => ({
            ...prev,
            trustedCirclePersonIds,
            networkSeedPersonIds: trustedCirclePersonIds,
        }))
    }, [trustedCirclePersonIds])

    const handleTrustedCircleChange = useCallback((nextIds: string[]) => {
        const normalizedIds = Array.from(new Set(nextIds))
        lastSyncedTrustedCircleRef.current = normalizedIds
        setFilters((prev) => {
            const seedsAreUnchanged =
                areStringArraysEqual(
                    prev.trustedCirclePersonIds,
                    normalizedIds,
                ) &&
                areStringArraysEqual(prev.networkSeedPersonIds, normalizedIds)

            if (seedsAreUnchanged) {
                return prev
            }

            return {
                ...prev,
                trustedCirclePersonIds: normalizedIds,
                networkSeedPersonIds: normalizedIds,
            }
        })
    }, [])

    useEffect(() => {
        if (!savedFilters) {
            return
        }
        isSyncingFiltersRef.current = true
        setFilters(savedFilters)
    }, [savedFilters])

    useEffect(() => {
        if (isSyncingFiltersRef.current) {
            isSyncingFiltersRef.current = false
            return
        }
        onFiltersChange?.(filters)
    }, [filters, onFiltersChange])

    return (
        <>
            {isFiltersModalOpen && (
                <SearchFiltersModal
                    filters={filters}
                    networkSeedPersons={networkSeedPersons}
                    onOpenTrustedCircleModal={onOpenTrustedCircleModal}
                    onClose={handleFiltersModalClose}
                    onSave={handleFiltersSave}
                    onSearchWithAi={handleSearchWithAi}
                    onSearch={onSearch}
                    onSaveFilters={onSaveFilters}
                    hasRoleContext={Boolean(roleId)}
                    savedJobDescription={roleJobDescription}
                    experienceMinYears={experienceMinYears}
                    experienceMaxYears={experienceMaxYears}
                    defaultMinExperienceYears={defaultMinExperienceYears}
                    defaultMaxExperienceYears={defaultMaxExperienceYears}
                    onExperienceMinYearsChange={onExperienceMinYearsChange}
                    onExperienceMaxYearsChange={onExperienceMaxYearsChange}
                    isSavingFilters={isSavingFilters}
                    filtersModalError={filtersModalError}
                    onTrustedCircleChange={handleTrustedCircleChange}
                    networkLinksPersonLiteCache={networkLinksPersonLiteCache}
                />
            )}
            <Card className="">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        <FontAwesomeIcon
                            icon={faFilter}
                            className="text-ctxt-blue mr-2"
                        />
                        Filters
                    </div>
                    <div>
                        <Button
                            onClick={() => setIsFiltersModalOpen(true)}
                            type="button"
                            style="primary"
                        >
                            <FontAwesomeIcon
                                icon={faSliders}
                                className="mr-2"
                            />
                            Edit Filters
                        </Button>
                    </div>
                </div>
                <div className="mt-3">
                    <ExpandableText
                        maxLines={3}
                        maxHeightPx={networkSeedsMaxHeightPx}
                        className="mt-1"
                    >
                        <div className="flex flex-wrap gap-2">
                            {networkSeedsForDisplay.length > 0 ? (
                                networkSeedsForDisplay.map(
                                    (personId, index) => {
                                        const person = networkSeedPersons.find(
                                            (p) => p.id === personId,
                                        )
                                        return person ? (
                                            <PersonBadge
                                                key={`${personId}-${index}`}
                                                person={person}
                                                className="bg-gray-50"
                                            />
                                        ) : (
                                            <Badge
                                                key={`${personId}-${index}`}
                                                color={BadgeColor.BrandBlue}
                                                className="text-sm"
                                                borderless={true}
                                            >
                                                {personId}
                                            </Badge>
                                        )
                                    },
                                )
                            ) : (
                                <Badge
                                    color={BadgeColor.Neutral2}
                                    className="text-sm"
                                    borderless={true}
                                >
                                    No Trusted Circle seeds selected
                                </Badge>
                            )}
                        </div>
                    </ExpandableText>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                    {filters.jobTitles.include.map((title, index) => (
                        <IncludeJobTitleBadge
                            key={`${title}-${index}`}
                            text={title}
                        />
                    ))}
                    {filters.jobTitles.exclude.map((title, index) => (
                        <ExcludeJobTitleBadge
                            key={`${title}-${index}`}
                            text={title}
                        />
                    ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {filters.keywordGroups.map((group, index) => {
                        const keywords = group.keywords
                            .map((keyword) => keyword.trim())
                            .filter(Boolean)

                        if (keywords.length === 0) {
                            return null
                        }

                        const label = keywords.join(' OR ')
                        return (
                            <KeywordGroupBadge
                                key={`${keywords.join('-')}-${index}`}
                                text={label}
                            />
                        )
                    })}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {filters.locations.map((location, index) => (
                        <LocationBadge
                            key={`${location}-${index}`}
                            text={location}
                        />
                    ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {filters.companies.include.map((title, index) => (
                        <IncludeCompanyBadge
                            key={`${title}-${index}`}
                            text={title}
                        />
                    ))}
                    {filters.companies.exclude.map((title, index) => (
                        <ExcludeCompanyBadge
                            key={`${title}-${index}`}
                            text={title}
                        />
                    ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {filters.seniorities.map((title, index) => (
                        <SeniorityBadge
                            key={`${title}-${index}`}
                            text={title}
                        />
                    ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {filters.jobFunctions.map((title, index) => (
                        <JobFunctionBadge
                            key={`${title}-${index}`}
                            text={title}
                        />
                    ))}
                </div>
                {experienceBadgeText && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        <ExperienceBadge text={experienceBadgeText} />
                    </div>
                )}
            </Card>
        </>
    )
}
